/**
 * One "Give feedback" dialog for the whole app, opened from many doors.
 *
 * `GiveFeedbackProvider` sits in the app shell around the page outlet and owns the one
 * dialog. Any entry calls `useGiveFeedback().open({...})` with what it knows (surface,
 * case, line, type, topic) or renders a `GiveFeedbackButton`. Outside the provider (a
 * test render of a single panel) the hook returns null and the button renders nothing.
 */

import { Icon, MessageSquarePlus } from "@norican/icons";
import { Button } from "@norican/react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { logger } from "@/lib/logger";

import { FeedbackDialog, type GiveFeedbackOptions } from "./feedback-dialog";

export interface GiveFeedback {
  open: (options: GiveFeedbackOptions) => void;
}

const GiveFeedbackContext = createContext<GiveFeedback | null>(null);

export function GiveFeedbackProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<{ options: GiveFeedbackOptions; key: number } | null>(
    null,
  );

  const open = useCallback((options: GiveFeedbackOptions) => {
    logger.debug("[feedback] open", {
      surface: options.surface,
      case: options.caseId ?? null,
      line: options.lineIndex ?? null,
      kind: options.kind ?? null,
      topic: options.topic ?? null,
    });
    setRequest((prev) => ({ options, key: (prev?.key ?? 0) + 1 }));
  }, []);
  const value = useMemo(() => ({ open }), [open]);

  return (
    <GiveFeedbackContext.Provider value={value}>
      {children}
      {request && (
        <FeedbackDialog
          key={request.key}
          open
          onOpenChange={(next) => {
            if (!next) {
              setRequest(null);
            }
          }}
          target={request.options}
        />
      )}
    </GiveFeedbackContext.Provider>
  );
}

/** The opener of the dialog, or null outside the app shell. */
export function useGiveFeedback(): GiveFeedback | null {
  return useContext(GiveFeedbackContext);
}

export interface GiveFeedbackButtonProps {
  options: GiveFeedbackOptions;
  /** The button text; "Give feedback" by default. */
  label?: string;
  variant?: "ghost" | "outline";
  fullWidth?: boolean;
  className?: string;
}

/** A small entry button. Renders nothing outside the provider. */
export function GiveFeedbackButton({
  options,
  label = "Give feedback",
  variant = "ghost",
  fullWidth,
  className,
}: GiveFeedbackButtonProps) {
  const give = useGiveFeedback();
  if (!give) {
    return null;
  }
  return (
    <Button
      size="sm"
      variant={variant}
      tone="neutral"
      fullWidth={fullWidth}
      className={className}
      startIcon={<Icon icon={MessageSquarePlus} size="xs" />}
      onClick={() => give.open(options)}
    >
      {label}
    </Button>
  );
}
